import { env } from '~/helpers/env'

/** La réponse de l'API, quel que soit son statut : c'est à l'appelant de dire ce qu'un refus veut dire. */
export type UploadResponse = {
  status: number
  /** Le corps JSON, ou `null` s'il n'en est pas un. */
  body: unknown
}

export type UploadListeners = {
  /** Octets envoyés sur le total, au fil de l'envoi. */
  onProgress?: (loaded: number, total: number) => void
  /** Tout est parti : le serveur traite, la réponse viendra plus tard. */
  onSent?: () => void
  signal?: AbortSignal
}

/** L'envoi a été annulé (`signal`). */
export class UploadAbortedError extends Error {
  constructor() {
    super('upload aborted')
    this.name = 'UploadAbortedError'
  }
}

/** Aucune réponse : réseau coupé, serveur injoignable, ou proxy qui ferme la connexion. */
export class UploadNetworkError extends Error {
  constructor() {
    super('upload network error')
    this.name = 'UploadNetworkError'
  }
}

function parseBody(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/**
 * Envoie un formulaire à l'API en rapportant la progression (#310).
 *
 * `XMLHttpRequest` et non `fetch` : `fetch` ne donne aucune progression
 * d'envoi, et un package de 500 Mo peut prendre plusieurs minutes. Résout sur
 * toute réponse HTTP, rejette seulement quand il n'y en a pas (annulation, réseau).
 */
export function uploadWithProgress(
  path: string,
  form: FormData,
  { onProgress, onSent, signal }: UploadListeners = {},
): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadAbortedError())
      return
    }
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${env.api.url}${path}`)
    xhr.withCredentials = true

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded, event.total)
    }
    // Retiré à la fin de la requête : un signal qui survit à l'envoi ne doit
    // plus rien pouvoir lui faire.
    const abort = () => xhr.abort()
    const settle = (done: () => void) => () => {
      signal?.removeEventListener('abort', abort)
      done()
    }
    xhr.upload.onload = () => onSent?.()
    xhr.onload = settle(() => resolve({ status: xhr.status, body: parseBody(xhr.responseText) }))
    xhr.onerror = settle(() => reject(new UploadNetworkError()))
    xhr.onabort = settle(() => reject(new UploadAbortedError()))
    signal?.addEventListener('abort', abort, { once: true })

    xhr.send(form)
  })
}
